import { useCallback, useEffect, useState } from 'react';
import { Button, Popconfirm, Space, Table, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
import { deleteWorkList, fetchWorkLists } from './api';
import { formatDateTimeShort } from './constants';
import type { WorkListSummary } from './types';

const { Title } = Typography;

export default function WorkListsManagementPage() {
  const [workLists, setWorkLists] = useState<WorkListSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const loadWorkLists = useCallback(async () => {
    setLoading(true);
    try {
      setWorkLists(await fetchWorkLists({ sort: 'created_at', order: 'desc' }));
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke hente arbejdslister');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadWorkLists();
  }, [loadWorkLists]);

  const handleDelete = async (workList: WorkListSummary) => {
    setDeletingId(workList.id);
    try {
      await deleteWorkList(workList.id);
      setWorkLists((current) => current.filter((item) => item.id !== workList.id));
      message.success(`Arbejdslisten "${workList.display_name}" er slettet`);
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke slette arbejdslisten');
    } finally {
      setDeletingId(null);
    }
  };

  const columns: ColumnsType<WorkListSummary> = [
    {
      title: 'Arbejdsliste',
      dataIndex: 'display_name',
      key: 'display_name'
    },
    {
      title: 'Virksomheder',
      dataIndex: 'item_count',
      key: 'item_count',
      width: 140
    },
    {
      title: 'Oprettet',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 190,
      render: (value: string) => formatDateTimeShort(value)
    },
    {
      title: '',
      key: 'actions',
      width: 100,
      align: 'right',
      render: (_value, workList) => (
        <Popconfirm
          title="Slet arbejdsliste?"
          description={`"${workList.display_name}" og dens rækker slettes permanent.`}
          okText="Slet"
          cancelText="Annuller"
          okButtonProps={{ danger: true, loading: deletingId === workList.id }}
          onConfirm={() => handleDelete(workList)}
        >
          <Button
            danger
            type="text"
            icon={<DeleteOutlined />}
            loading={deletingId === workList.id}
            aria-label={`Slet ${workList.display_name}`}
            title="Slet arbejdsliste"
          />
        </Popconfirm>
      )
    }
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
        <Title level={3} style={{ margin: 0 }}>Arbejdslister</Title>
        <Button icon={<ReloadOutlined />} onClick={() => void loadWorkLists()} loading={loading}>
          Opdater
        </Button>
      </Space>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={workLists}
        loading={loading}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        locale={{ emptyText: 'Der er ingen gemte arbejdslister' }}
      />
    </Space>
  );
}